import type { RxCollection, MangoQuerySelector } from 'rxdb';
import type {
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType,
    ParcelDocType,
    ProjectDocType,
    MediaDocType,
    MediaLinkDocType,
    MediaGroupDocType
} from '@ast-data/shared';
import { composeFileName, fileNameSpan } from '../../../utils/downloadFile';
import { familyProjectIds } from '../../projects/family';
import {
    ALL_LAYERS,
    findPenPreset,
    MARK,
    PEN_PRESETS
} from '../../media/sketch/constants';
import { areaFillFromCode } from '../../media/sketch/areaFill';
import type { AreaFill, Layer, SceneData } from '../../media/sketch/types';
import { locationLabel as sharedLageLabel } from '../../findings/locationLabel';
import { strataSlotKey } from './findingPdfText';
import {
    findPlacedMedia,
    resolveExportMedia,
    type ExportMedia
} from '../pdf/exportMedia';
import {
    byMeasureThen,
    measureFileNamePart,
    projectsById,
    type ExportScope
} from '../pdf/exportScope';

import { compareNatural } from '../../../utils/compare';
import { showDocs } from '../../users/personLabels';
import {
    createDeliverableTexts,
    type DeliverableTexts
} from '../../translation/deliverableTexts';

/**
 * Getting the pictures in is the same job on every sheet, so it lives in the
 * kit — re-exported here because this module's own consumers (SketchBlock, the
 * legend helpers below) have always spoken of them as the export's vocabulary.
 */
export {
    parseScene,
    resolveMediaDataUrl,
    type ExportMedia
} from '../pdf/exportMedia';

/**
 * Data layer for the Befundbeschreibung PDF export: `collectFindingExportData`
 * gathers what a description page needs — the finding with its Planum/Profil
 * docs, the Flurstück, the images as data-URLs (offline-first) and a
 * Lagezuordnung label — into a plain, RxDB-free structure. The layout is in
 * FindingPdfDocument.tsx; this file is only the plumbing.
 */

/** One Planum or Profil description = one page (or more) in the PDF. */
export interface ExportDescription {
    /** Stable React key. */
    key: string;
    kind: 'planum' | 'profile';
    /** Exactly one of these is set, matching `kind`. */
    planum: FindingPlanumDocType | null;
    profile: FindingProfileDocType | null;
    /** The description's Flurstück (via parcelId), or null. */
    parcel: ParcelDocType | null;
    /** Sketches + photos of this description, createdAt-ascending. */
    media: ExportMedia[];
    /**
     * Number of the Planum the "Differenz zu Pl. …" (Abstich) refers to: the
     * next-lower Planum of the same Befund, as the form dialog picks it. Null
     * for the first Planum, for a numberless predecessor, and for Profils —
     * the measure is then left off the sheet entirely, having nothing to name.
     */
    previousPlanumNumber: string | null;
    /** Footer Lagezuordnung, e.g. "Bef. 7000, Fl. 1, Pl. 1". */
    locationLabel: string;
}

/** A finding with its descriptions, in page order (Planums, then Profils). */
export interface ExportFinding {
    finding: FindingDocType;
    /**
     * The Maßnahme THIS Befund belongs to — carried per record, not per export,
     * so a selection made in the cross-project Auswertung prints as one
     * documentation whose sheets each say where their Befund is from.
     */
    project: ProjectDocType;
    descriptions: ExportDescription[];
}

/** The whole payload for one export run. */
export interface FindingExportData {
    /** findingId → the finding number, for resolving relation/reference labels. */
    findingNumberById: Map<string, string>;
    /**
     * Planum-/Profil-id → its Lagezuordnung ("Bef. 7000, Fl. 1, Pl. 1"), for
     * the `identicalWith*Ids` refs. Covers the WHOLE Teilmaßnahme family, not
     * just the exported findings — an identity ref may point into another phase.
     */
    descriptionLabelById: Map<string, string>;
    /**
     * The SAME labels, keyed by the slot a positional relation names them in
     * (see strataSlotKey): „liegt über/schneidet" and its four siblings store
     * the bare Befundnummer, and mean the Befund described on this Fläche and
     * Planum-/Profilnummer.
     */
    strataLabelBySlot: Map<string, string>;
    findings: ExportFinding[];
    /**
     * Selected Befunde left out because their Maßnahme is not in the local
     * database — so "nothing to print" can say why.
     */
    missingProjectCount: number;
}

/** The collections `collectFindingExportData` reads (from useRxCollection). */
export interface ExportCollections {
    projects: RxCollection<ProjectDocType>;
    findings: RxCollection<FindingDocType>;
    planums: RxCollection<FindingPlanumDocType>;
    profiles: RxCollection<FindingProfileDocType>;
    parcels: RxCollection<ParcelDocType>;
    media: RxCollection<MediaDocType>;
    mediaLinks: RxCollection<MediaLinkDocType>;
    mediaGroups: RxCollection<MediaGroupDocType>;
}

/** One SYMBOL entry of a sketch's Legende — a mark whose SHAPE is its meaning. */
export interface SketchLegendSymbol {
    /** Which mark it stands for; the layout draws that shape (see SketchBlock). */
    kind: 'nail' | 'stratum';
    /** The colour those marks were drawn in — the commonest, when they differ. */
    color: string;
    /** i18n key, e.g. "sketch.nail". */
    labelId: string;
}

/** One pen entry of a sketch's Legende (untranslated; the layout resolves labelId). */
export interface SketchLegendPen {
    color: string;
    /** Dash pattern in multiples of the line width; absent = a solid line. */
    dash?: number[];
    /** Drawn as a filled area rather than a line (Störung, Humus). */
    fill?: AreaFill;
    /** i18n key, e.g. "sketch.find.HK". */
    labelId: string;
}

function numberCompare(left: string, right: string): number {
    return compareNatural(left, right);
}

function groupBy<T>(rows: T[], keyOf: (row: T) => string): Map<string, T[]> {
    const map = new Map<string, T[]>();
    for (const row of rows) {
        const key = keyOf(row);
        const list = map.get(key) ?? [];
        list.push(row);
        map.set(key, list);
    }
    return map;
}

/**
 * The distinct PENS a sketch was drawn with (matched to PEN_PRESETS), for a
 * Legende. The layout translates each `labelId`.
 *
 * A pen is a colour AND a line pattern, so the Profilkasten and the
 * Schichtgrenze are legended as themselves rather than collapsing into the
 * Befundkontur they share an ink with. Marks that are not lines carry a colour
 * only, and match the solid pen of that colour.
 *
 * EVERY preset that was drawn with, not just the find types — a drawing in the
 * default ink is the commonest sheet there is, and it used to print with no
 * Legende at all. In PEN_PRESETS order; a pen that matches no preset is left
 * out, there being nothing to call it.
 */
export function sketchPenLegend(scene: SceneData | null): SketchLegendPen[] {
    if (!scene) {
        return [];
    }
    const used = new Set<string>();
    const add = (color?: string, dash?: number[], fill?: AreaFill) => {
        const preset = color ? findPenPreset(color, dash, fill) : undefined;
        if (preset) {
            used.add(preset.id);
        }
    };
    /**
     * Only a stroke can carry an area fill, which is what keeps a house-ink nail
     * resolving to the Befundkontur rather than to the Humus.
     *
     * What is NOT on the sheet is not in the Legende: a hidden mark, one on a
     * layer switched off, and an eraser stroke — which carries the colour of
     * whatever pen was in hand and would name a line that is not there.
     */
    visibleMarks(scene, 'drawing', scene.strokes).forEach((stroke) => {
        if (stroke.e !== 1) {
            add(stroke.c, stroke.d, areaFillFromCode(stroke.f));
        }
    });
    visibleMarks(scene, 'symbols', scene.nails).forEach((nail) => add(nail.c));
    visibleMarks(scene, 'leaders', scene.leaders).forEach((leader) =>
        add(leader.c)
    );
    visibleMarks(scene, 'text', scene.texts).forEach((text) => add(text.c));
    visibleMarks(scene, 'symbols', scene.strata).forEach((stratum) =>
        add(stratum.c)
    );

    return PEN_PRESETS.filter((preset) => used.has(preset.id)).map(
        (preset) => ({
            color: preset.color,
            ...(preset.dash ? { dash: preset.dash } : {}),
            ...(preset.fill ? { fill: preset.fill } : {}),
            labelId: preset.labelId
        })
    );
}

/**
 * The marks of one layer the drawing actually SHOWS: the layer is on and the
 * mark is not hidden. `scene.layers` lists the VISIBLE layers; absent or empty
 * means all of them, as every scene written before the toggles carries (the
 * editor reads it the same way — see visibleLayersFromScene).
 */
function visibleMarks<T extends { h?: 0 | 1 }>(
    scene: SceneData,
    layer: Layer,
    marks: T[] | undefined
): T[] {
    const layers = scene.layers?.filter((entry) => ALL_LAYERS.includes(entry));
    const shown = !layers || layers.length === 0 || layers.includes(layer);
    if (!shown) {
        return [];
    }
    return (marks ?? []).filter((mark) => mark.h !== 1);
}

/**
 * The SYMBOLS a sketch carries — the Nagel and the Schicht — for the Legende.
 * The pen legend above says what a COLOUR means and cannot say what a SHAPE
 * means, and these two are shapes: a crosshair is a levelled point, a boxed
 * number the layer it stands in.
 *
 * ONE entry per kind, not per colour: the colour of a nail is whatever pen was
 * in hand, the shape is what it means. The commonest colour draws the swatch,
 * ties going to the first one placed.
 */
export function sketchSymbolLegend(
    scene: SceneData | null
): SketchLegendSymbol[] {
    if (!scene) {
        return [];
    }
    const entry = (
        kind: SketchLegendSymbol['kind'],
        labelId: string,
        marks: { c?: string }[]
    ): SketchLegendSymbol[] => {
        if (marks.length === 0) {
            return [];
        }
        const counts = new Map<string, number>();
        for (const mark of marks) {
            // Legacy marks carry no colour: they were drawn before the picker,
            // and the canvas still draws them in the default red.
            const color = mark.c ?? MARK;
            counts.set(color, (counts.get(color) ?? 0) + 1);
        }
        let color = MARK;
        let best = 0;
        for (const [candidate, count] of counts) {
            if (count > best) {
                color = candidate;
                best = count;
            }
        }
        return [{ kind, color, labelId }];
    };
    return [
        ...entry(
            'nail',
            'sketch.nail',
            visibleMarks(scene, 'symbols', scene.nails)
        ),
        ...entry(
            'stratum',
            'sketch.stratum',
            visibleMarks(scene, 'symbols', scene.strata)
        )
    ];
}

export function sketchTextMarks(
    scene: SceneData | null
): { text: string; color: string }[] {
    if (!scene) {
        return [];
    }
    const marks: { text: string; color: string }[] = [];
    const push = (text?: string, color?: string) => {
        const value = (text ?? '').trim();
        if (value) {
            marks.push({ text: value, color: color ?? '#d32f2f' });
        }
    };
    scene.nails?.forEach((nail) => push(nail.t, nail.c));
    scene.leaders?.forEach((leader) => push(leader.t, leader.c));
    scene.texts?.forEach((text) => push(text.t, text.c));
    return marks;
}

/**
 * The footer Lagezuordnung of one page, e.g. "Bef. 7000, Fl. 1, Pl. 1". Reads
 * the description's spatial slot and defers to the shared grammar — the same
 * line the finding reference chips show.
 */
export function locationLabel(
    findingNumber: string,
    description: ExportDescription
): string {
    const spatial = description.planum ?? description.profile ?? null;
    return sharedLageLabel({
        findingNumber,
        area: spatial?.area,
        planumNumber: description.planum?.planumNumber,
        profileNumber: description.profile?.profileNumber,
        part: spatial?.partLabel
    });
}

/**
 * The name the Befunddokumentation downloads as, in the shared export grammar
 * ({@link composeFileName}): the Maßnahme, the subject, then WHICH Befunde are
 * in it ({@link fileNameSpan}) —
 *
 * - one        → "M-2024-0123-1_Befunddokumentation_Bef-7000.pdf"
 * - two        → "M-2024-0123-1_Befunddokumentation_Bef-7000_Bef-7001.pdf"
 * - three plus → "M-2024-0123-1_Befunddokumentation_Bef-7000_Bef-7042_12Befunde.pdf"
 *
 * The Befund number is what a person looks a sheet up by, so a documentation
 * exported for ONE of them says so instead of being told apart from the whole
 * project's only by the clock at the head.
 *
 * `findingNumbers` in the order the document prints them (number-ascending).
 * German throughout, independent of the UI language. `measureNumbers` is one per
 * Befund; a documentation spanning several Maßnahmen is named by how many rather
 * than by the first (see measureFileNamePart).
 */
export function findingPdfFileName(
    measureNumbers: readonly string[],
    findingNumbers: readonly string[]
): string {
    const labels = findingNumbers.map((number) => {
        const trimmed = number.trim();
        return trimmed ? `Bef-${trimmed}` : '';
    });
    return composeFileName(
        [
            measureFileNamePart(measureNumbers),
            'Befunddokumentation',
            ...fileNameSpan(labels, 'Befunde')
        ],
        '.pdf'
    );
}

/** A collection's matching documents, people shown by their accounts. */
async function findAllShown<T>(
    collection: RxCollection<T>,
    selector: Record<string, unknown>,
    labelOf?: (id: string) => string
): Promise<T[]> {
    const docs = await collection
        .find({ selector: selector as MangoQuerySelector<T> })
        .exec();
    return showDocs<T>(collection.name, docs, labelOf);
}

/**
 * Gather the export payload for a set of Befunde — every one of a Maßnahme, or
 * a named set which may span Maßnahmen (the cross-project Auswertung ticks them
 * there). Each Befund carries its own project, so every sheet says where it is
 * from.
 *
 * The Teilmaßnahme families — for the shared parcels, and for the description
 * labels a stratigraphic reference may point into — are resolved from every
 * project involved.
 *
 * Befunde whose Maßnahme is not in the local database are DROPPED: the sheet's
 * whole head comes from the project, so there is nothing to print for them.
 */
export async function collectFindingExportData({
    collections,
    scope,
    labelOf,
    deliverable = createDeliverableTexts(null, null)
}: {
    collections: ExportCollections;
    scope: ExportScope;
    /** Shows linked people by their accounts — see usePersonLabels. */
    labelOf?: (id: string) => string;
    /** Puts the accepted German translations in; without, the originals. */
    deliverable?: DeliverableTexts;
}): Promise<FindingExportData> {
    const findAll = <T>(
        collection: RxCollection<T>,
        selector: Record<string, unknown>
    ) => findAllShown<T>(collection, selector, labelOf);
    const allProjects = await findAll<ProjectDocType>(collections.projects, {});
    const projectById = projectsById(allProjects);

    const selected = await findAll<FindingDocType>(
        collections.findings,
        scope.kind === 'project'
            ? { projectId: scope.projectId }
            : { id: { $in: [...scope.ids] } }
    );
    // Grouped by Maßnahme, then by number: within ONE Maßnahme this is exactly
    // the old order, so a project-level documentation comes out unchanged.
    const findings = selected
        .filter((finding) => projectById.has(finding.projectId))
        .sort(
            byMeasureThen(
                (finding) =>
                    projectById.get(finding.projectId)?.measureNumber ?? '',
                (firstFinding, secondFinding) =>
                    numberCompare(
                        firstFinding.findingNumber,
                        secondFinding.findingNumber
                    )
            )
        );
    if (findings.length === 0) {
        return {
            findingNumberById: new Map(),
            descriptionLabelById: new Map(),
            strataLabelBySlot: new Map(),
            findings: [],
            missingProjectCount: selected.length
        };
    }
    // Every family any of them belongs to — a Befund is shared across its
    // Teilmaßnahme family, and a reference may point into a sibling phase.
    const familyIds = [
        ...new Set(
            findings.flatMap((finding) =>
                familyProjectIds(
                    allProjects,
                    projectById.get(finding.projectId)?.familyId ?? '',
                    finding.projectId
                )
            )
        )
    ];

    const ids = findings.map((finding) => finding.id);
    const [loadedPlanums, loadedProfiles, parcels] = await Promise.all([
        findAll<FindingPlanumDocType>(collections.planums, {
            findingId: { $in: ids }
        }),
        findAll<FindingProfileDocType>(collections.profiles, {
            findingId: { $in: ids }
        }),
        findAll<ParcelDocType>(collections.parcels, {
            projectId: { $in: familyIds }
        })
    ]);
    // The sheets print a description's two long texts, in German where an
    // accepted translation has them so.
    const planums = await deliverable.apply('findingPlanum', loadedPlanums);
    const profiles = await deliverable.apply('findingProfile', loadedProfiles);

    const parcelById = new Map(parcels.map((parcel) => [parcel.id, parcel]));
    const findingNumberById = new Map(
        findings.map((finding) => [finding.id, finding.findingNumber])
    );

    // "Teil von / identisch mit" references a SPECIFIC description, which may
    // live on a finding that isn't part of this export (or in another phase of
    // the family). Label every description in the family so those refs resolve
    // instead of printing bare ids.
    const familyFindings = await findAll<FindingDocType>(collections.findings, {
        projectId: { $in: familyIds }
    });
    const familyFindingIds = familyFindings.map((finding) => finding.id);
    const familyNumberById = new Map(
        familyFindings.map((finding) => [finding.id, finding.findingNumber])
    );
    const [familyPlanums, familyProfiles] = await Promise.all([
        findAll<FindingPlanumDocType>(collections.planums, {
            findingId: { $in: familyFindingIds }
        }),
        findAll<FindingProfileDocType>(collections.profiles, {
            findingId: { $in: familyFindingIds }
        })
    ]);
    const descriptionLabelById = new Map<string, string>();
    // The same labels a second time, under their slot: it is all the positional
    // relations have to go by, which store a Befundnummer and nothing else.
    const strataLabelBySlot = new Map<string, string>();
    /** First description of a slot wins — a Befund taken in Teilen names ONE Lage. */
    const rememberSlot = (
        kind: 'planum' | 'profile',
        area: string,
        number: string,
        findingNumber: string,
        label: string
    ) => {
        if (!findingNumber) {
            return;
        }
        const key = strataSlotKey({ kind, area, number, findingNumber });
        if (!strataLabelBySlot.has(key)) {
            strataLabelBySlot.set(key, label);
        }
    };
    for (const planum of familyPlanums) {
        const findingNumber = familyNumberById.get(planum.findingId) ?? '';
        const label = sharedLageLabel({
            findingNumber,
            area: planum.area,
            planumNumber: planum.planumNumber
        });
        descriptionLabelById.set(planum.id, label);
        rememberSlot(
            'planum',
            planum.area,
            planum.planumNumber,
            findingNumber,
            label
        );
    }
    for (const profile of familyProfiles) {
        const findingNumber = familyNumberById.get(profile.findingId) ?? '';
        const label = sharedLageLabel({
            findingNumber,
            area: profile.area,
            profileNumber: profile.profileNumber
        });
        descriptionLabelById.set(profile.id, label);
        rememberSlot(
            'profile',
            profile.area,
            profile.profileNumber,
            findingNumber,
            label
        );
    }

    // Media for every description, resolved to data-URLs in parallel.
    const descriptionIds = [
        ...planums.map((planum) => planum.id),
        ...profiles.map((profile) => profile.id)
    ];
    const mediaFor = await resolveExportMedia(
        await findPlacedMedia(
            collections.media,
            collections.mediaLinks,
            collections.mediaGroups,
            descriptionIds
        )
    );

    const planumsByFinding = groupBy(planums, (planum) => planum.findingId);
    const profilesByFinding = groupBy(profiles, (profile) => profile.findingId);

    const exportFindings: ExportFinding[] = findings.map((finding) => {
        const findingNumber = finding.findingNumber;
        const own = [
            ...(planumsByFinding.get(finding.id) ?? [])
                .slice()
                .sort((firstPlanum, secondPlanum) =>
                    numberCompare(
                        firstPlanum.planumNumber,
                        secondPlanum.planumNumber
                    )
                )
                // Sorted by number just above, so the predecessor is simply the
                // entry before this one.
                .map<ExportDescription>((planum, index, sorted) => {
                    const previous = index > 0 ? sorted[index - 1] : null;
                    const description: ExportDescription = {
                        key: `planum:${planum.id}`,
                        kind: 'planum',
                        planum,
                        profile: null,
                        parcel: parcelById.get(planum.parcelId) ?? null,
                        media: mediaFor(planum.id),
                        previousPlanumNumber:
                            previous?.planumNumber.trim() || null,
                        locationLabel: ''
                    };
                    description.locationLabel = locationLabel(
                        findingNumber,
                        description
                    );
                    return description;
                }),
            ...(profilesByFinding.get(finding.id) ?? [])
                .slice()
                .sort((firstProfile, secondProfile) =>
                    numberCompare(
                        firstProfile.profileNumber,
                        secondProfile.profileNumber
                    )
                )
                .map<ExportDescription>((profile) => {
                    const description: ExportDescription = {
                        key: `profile:${profile.id}`,
                        kind: 'profile',
                        planum: null,
                        profile,
                        parcel: parcelById.get(profile.parcelId) ?? null,
                        media: mediaFor(profile.id),
                        previousPlanumNumber: null,
                        locationLabel: ''
                    };
                    description.locationLabel = locationLabel(
                        findingNumber,
                        description
                    );
                    return description;
                })
        ];
        return {
            finding,
            // Present — `findings` dropped every Befund whose project is not.
            project: projectById.get(finding.projectId) as ProjectDocType,
            descriptions: own
        };
    });

    return {
        findingNumberById,
        descriptionLabelById,
        strataLabelBySlot,
        findings: exportFindings,
        missingProjectCount: selected.length - findings.length
    };
}
